<template>
  <div class="boost-filter">
    <div
      v-if="filter.length"
      class="boost-filter__inner"
    >
      <div
        v-if="!isMobile && choicesArray.length"
        class="boost-filter__ancillary"
      >
        <span
          v-html="$string(
            'collection.filter_product_count_html',
            { replace: { count: totalProduct }}
          )"
        />
        <btn
          modifiers="text"
          :show-icon="false"
          :label="shopAllString"
          @click="clearChoices"
        />
      </div>

      <div class="boost-filter__facets-wrap">
        <span
          class="boost-filter__facets-title"
          v-text="`${$string('collection.filter')}:`"
        />

        <template
          v-for="facet of filter"
          :key="facet.filterOptionId"
        >
          <div
            v-if="facet?.values?.length"
            class="boost-filter__facet"
          >
            <boost-facet
              :facet="facet"
              :choices="dataChoices"
              @choices-updated="choicesUpdated"
              @facets-ready="facetsReady"
            />
          </div>
        </template>
      </div>

      <span><icon-pipe /></span>

      <boost-sort-by
        v-if="!isMobile"
        :sort-option="sortOption"
        :data-choices="dataChoices"
        :sort-by-options="getActiveOptions"
        @handle-sort-by-change="handleSortByChange"
      />
    </div>

    <div
      v-else
      class="boost-filter__inner"
    >
      <div class="boost-filter__facets-wrap">
        <div
          v-for="(index) of 5"
          :key="index"
          class="boost-filter__facet"
          :class="[$class('loading')]"
          :style="{ animationDelay: animationDelay(index) }"
        />
      </div>

      <div
        class="boost-filter__sort-by"
        :class="[$class('loading')]"
      />
    </div>

    <boost-active-values
      v-if="hasNonPageValue"
      :choices="choicesArray"
      :sort-by-options="getActiveOptions"
      @choices-updated="choicesUpdated"
      @clear-choices="clearChoices"
    />
  </div>
</template>

<script>

/**
 * Vue: Boost filter (main-collection)
 * -----------------------------------------------------------------------------
 * Collection and search filters.
 *
 * @param {String} choices - Available choices.
 * @param {String} title - Collection Title.
 * @param {Array} filter - Array of filters.
 * @param {Object} sortByOptions - Options for sorting.
 * @param {Number} totalProduct - Total number of products.
 *
 */
import { mapState } from 'vuex'

import BoostActiveValues from '~async/main-collection/boost-active-values'
import BoostFacet from '~async/main-collection/boost-facet'
import BoostSortBy from '~async/main-collection/boost-sort-by'
import Btn from '~global/btn/btn'
import IconPipe from '~icons/misc/pipe.svg'

export default {
  name: 'BoostFilter',

  components: {
    BoostActiveValues,
    BoostFacet,
    BoostSortBy,
    Btn,
    IconPipe,
  },

  props: {
    choices: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    filter: {
      required: true,
      type: Array,
      default: () => [],
    },
    totalProduct: {
      type: Number,
      default: 0,
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'object',
    'clear-choices': () => true,
    'facets-ready': () => true,
  },

  data() {
    return {
      choicesArray: [],
      dataChoices: '',
      hasNonPageValue: false,
      sortOption: {
        id: '',
        label: '',
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute active options by filtering 'sort by' props based on those
     * enabled in section settings.
     * @returns {Object}
     */
    getActiveOptions() {
      return Object.values(this.sortByOptions).filter((object) => {
        return object.value === true
      })
    },

    /**
     * Compute 'Shop All' string with collection title, if it exists.'
     * @returns {Object}
     */
    shopAllString() {
      return this.$string(
        'collection.shop_all',
        { replace: { category: this.title ? this.title : '' } },
      )
    },
  },

  watch: {

    /**
     * Watch choices to update array.
     */
    choices(choices) {
      this.dataChoices = choices
      this.updateChoicesArray(choices)
    },
  },

  methods: {

    /**
     * Compute animation delay.
     * @param {Number} index - Index of element in loop.
     * @returns {String}
     */
    animationDelay(index) {
      return this.$timing('quick', { index, unit: 's' })
    },

    /**
     * Receive user input from facets and pass up to main collection.
     * @param {String} data - Formatted update to user choices.
     */
    choicesUpdated(data) {
      this.$emit('choices-updated', data)
    },

    /**
     * Notify when facets are present in the UI.
     */
    facetsReady() {
      this.$emit('facets-ready')
    },

    /**
     * Notify when 'clear all' button is clicked.
     */
    clearChoices() {
      this.$emit('clear-choices')
      this.hasNonPageValue = false

      this.sortOption = {
        id: '',
        label: '',
      }
    },

    /**
     * Update choices array passed to active values component on choices change.
     * @param {String} data - Choices string to split into an array.
     */
    updateChoicesArray(data) {
      this.choicesArray = data.split('&').filter((choice) => {
        return choice !== ''
      })

      if (!this.choicesArray.length) {
        this.hasNonPageValue = false

        this.sortOption = {
          id: '',
          label: '',
        }

        return
      }

      this.choicesArray.forEach((choice) => {
        if (!choice.includes('page')) {
          this.hasNonPageValue = true
        }

        if (choice.includes('sort')) {
          const value = choice.split('=')[1]

          const option = Object.values(this.sortByOptions).find((object) => {
            return object.id === value
          })

          const { id, label } = option

          this.sortOption = {
            id,
            label,
          }
        }
      })
    },

    /**
     * Update sort options on select change.
     * @param {Event} event - Change event.
     */
    handleSortByChange(event) {
      const nativeOptions = document.querySelectorAll('[js-boost-filter="sortByOption"]')
      let matchValue = event.currentTarget.value
      let action = 'add'

      if (event.currentTarget.classList.contains('boost-filter__sort-by-desktop--option')) {
        matchValue = event.currentTarget.id

        if (event.currentTarget.classList.contains(this.$class('active'))) {
          event.currentTarget.classList.remove(this.$class('active'))
          action = 'remove'

          nativeOptions.forEach((option) => {
            option.removeAttribute('selected')
          })

        } else {
          event.currentTarget.classList.add(this.$class('active'))

          nativeOptions.forEach((option) => {
            if (option.id === matchValue) {
              option.setAttribute('selected', 'selected')

            } else {
              option.removeAttribute('selected')
            }
          })
        }
      }

      const option = Object.values(this.sortByOptions).find((object) => {
        return object.id === matchValue
      })

      const { id, label } = option

      const data = {
        requestParam: `&sort=${id}`,
        action,
      }

      this.$emit('choices-updated', data)

      this.sortOption = {
        id,
        label,
      }
    },

    /**
     * Toggle active class on 'sort by' label on click.
     * @param {Object} event - Click event.
     */
    handleSelectLabelClick(event) {
      const sortBy = event.currentTarget.parentNode
      const options = sortBy.querySelectorAll('[js-boost-filter="sortByOption"]')

      if (!sortBy.classList.contains(this.$class('active'))) {
        const facets = document.querySelectorAll('[js-boost-facet="facet"]')

        facets.forEach((facet) => {
          const values = facet.querySelectorAll('[js-boost-facet="value"]')

          facet.classList.remove(this.$class('active'))
          values.forEach((value) => value.setAttribute('tabindex', -1))
        })

        sortBy.classList.add(this.$class('active'))
        options.forEach((option) => option.setAttribute('tabindex', 0))
        return
      }

      sortBy.classList.remove(this.$class('active'))
      options.forEach((option) => option.setAttribute('tabindex', -1))
    },

    /**
     * Return empty or full circle icon based on active status of option.
     * @param {String} sortId - Id of sort option.
     */
    getSortIcon(sortId) {
      if (this.dataChoices.includes(sortId)) {
        return 'icon-circle-full'
      }

      return 'icon-circle-empty'
    },
  },
}
</script>

<style lang="scss">
@import './boost-filter';
</style>
